export type MultiBuyerRole =
  | 'multiBuyer'
  | 'masterMultiBuyer'
  | 'mbManager'
  | 'none';
export type EntityType = 'owned' | 'servicePartner';
export type MultiBuyerCompany = 'yougov' | 'quantilope' | 'none';

export const MULTI_BUYER_ROLES: readonly MultiBuyerRole[] = [
  'multiBuyer',
  'masterMultiBuyer',
  'mbManager',
  'none',
];

export const MULTI_BUYER_ROLES_NUM_MASK: Record<MultiBuyerRole, number> = {
  multiBuyer: 6,
  mbManager: 6,
  masterMultiBuyer: 7,
  none: 0,
};

export function isMultiBuyerRole(value: unknown): value is MultiBuyerRole {
  return MULTI_BUYER_ROLES.includes(value as MultiBuyerRole);
}

export interface AccessibleEntity {
  id: number;
  type: EntityType;
}

export interface BuyerCapabilities {
  canManageBuyerSettings: boolean;
  canCreateTemplate: boolean;
  canSeeMarginAndBank: boolean;
  hasAccessToTemplate: boolean;
  canSeeCustomTemplates: boolean;
  canManageSupplierSettings: boolean;
  canSeeSupplierBlends: boolean;
  canSeeOperatorProjectManager: boolean;
  canSeeCrmId: boolean;
  canAccessMasterTemplate: boolean;
}

export interface AccessControlConfig {
  clientId: number;
  userRole: MultiBuyerRole;
  clientName: MultiBuyerCompany;
  buyerAcssLvls: number;
  multiBuyerCmps: Array<number>;
  capabilities: BuyerCapabilities;
  accessibleEntities: AccessibleEntity[];
}

const FULL_ACCESS_CAPABILITIES: BuyerCapabilities = {
  canSeeMarginAndBank: true,
  canCreateTemplate: true,
  canManageBuyerSettings: true,
  hasAccessToTemplate: true,
  canSeeCustomTemplates: false,
  canManageSupplierSettings: true,
  canSeeSupplierBlends: true,
  canAccessMasterTemplate: false,
  canSeeOperatorProjectManager: false,
  canSeeCrmId: false,
};

const LOCKED_CAPABILITIES: BuyerCapabilities = {
  canSeeMarginAndBank: false,
  canCreateTemplate: false,
  canManageBuyerSettings: false,
  hasAccessToTemplate: false,
  canSeeCustomTemplates: false,
  canManageSupplierSettings: false,
  canSeeSupplierBlends: false,
  canAccessMasterTemplate: false,
  canSeeOperatorProjectManager: false,
  canSeeCrmId: false,
};

export class AccessConfig implements AccessControlConfig {
  private constructor(
    readonly clientId: number,
    readonly userRole: MultiBuyerRole,
    readonly clientName: MultiBuyerCompany,
    readonly buyerAcssLvls: number,
    readonly multiBuyerCmps: number[],
    readonly capabilities: BuyerCapabilities,
    readonly accessibleEntities: AccessibleEntity[],
    readonly isReady: boolean
  ) {}

  static fromApiResponse(data: AccessControlConfig): AccessConfig {
    return new AccessConfig(
      data.clientId,
      data.userRole,
      data.clientName,
      data.buyerAcssLvls,
      data.multiBuyerCmps,
      data.capabilities,
      data.accessibleEntities,
      true
    );
  }

  static default(): AccessConfig {
    return new AccessConfig(
      0,
      'none',
      'none',
      0,
      [0],
      FULL_ACCESS_CAPABILITIES,
      [],
      true
    );
  }

  static uninitialized(): AccessConfig {
    return new AccessConfig(
      0,
      'none',
      'none',
      0,
      [],
      LOCKED_CAPABILITIES,
      [],
      false
    );
  }
}

export interface IMultiBuyerPermissions {
  canManageBuyerSettings(): boolean;
  canCreateTemplate(): boolean;
  canSeeMarginAndBank(): boolean;
  hasAccessToTemplate(): boolean;
  canSeeCustomTemplates(): boolean;
  canSeeOperatorProjectManager(): boolean;
  canSeeCrmId(): boolean;
  canManageSupplierSettings(): boolean;
  canSeeSupplierBlends(): boolean;
  canAccessMasterTemplate(): boolean;
}
